import React, { useState, useEffect } from 'react';
import { urls } from './config.dev';
import GiveFPmodal from './GiveFPmodal';
import { useNavigate } from 'react-router-dom';
import Topbar from './Topbar';
import MissingDrugs from './MissingDrugs'; 
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function NurseDashboard() {
  // State management
  const [fpPatients, setFpPatients] = useState([]);
  const [familyPlanningMethods, setFamilyPlanningMethods] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [clientStatuses, setClientStatuses] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [employeeName, setEmployeeName] = useState('');
  
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const urlToken = params.get('token');

  // Security check on component mount
  useEffect(() => {
    const fetchTokenAndCheckSecurity = async () => {
      try {
        const securityResponse = await fetch(urls.security, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: urlToken }),
        });

        if (securityResponse.ok) {
          const securityData = await securityResponse.json();
          if (securityData.message === 'Session valid') {
            setEmployeeName(securityData.employee_name);
            localStorage.setItem('token', securityData.clinic_session_token);
          } else if (securityData.error === 'Session expired') {
            navigate(`/dashboard?token=${securityData.clinic_session_token}`);
          } else {
            navigate('/login');
          }
        } else {
          throw new Error('Failed security check');
        }
      } catch (error) {
        console.error('Security error:', error);
        navigate('/login');
      }
    };

    fetchTokenAndCheckSecurity();
  }, [navigate, urlToken]);

  // Data fetching functions
  const fetchFamilyPlanningPatients = async () => {
    if (!urlToken) {
      setErrorMessage('Authentication required');
      return;
    }

    try {
      const response = await fetch(urls.fetchFPwaiting, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: urlToken }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.message) {
          setErrorMessage(data.message);
          setFpPatients([]);
        } else {
          setFpPatients(data.contacts || []);
          setErrorMessage('');
        }
      } else {
        throw new Error('Failed to fetch patients');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setErrorMessage('Failed to load patient data');
    }
  };

  const fetchAllMethods = async () => {
    try {
      const response = await fetch(urls.allFamilyPlanningMethods, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: urlToken }),
      });

      if (response.ok) {
        const methods = await response.json();
        setFamilyPlanningMethods(methods);
      } else {
        throw new Error('Failed to fetch methods');
      }
    } catch (error) {
      setErrorMessage('Failed to load methods');
    }
  };

  const fetchClientStatuses = async () => {
    if (!urlToken) {
      setErrorMessage('Authentication required');
      return;
    }

    try {
      const response = await fetch(urls.fetchFPstatus, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: urlToken }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.message) {
          setErrorMessage(data.message);
          setClientStatuses([]);
        } else {
          setClientStatuses(data.contacts || []);
          setErrorMessage('');
        }
      } else {
        throw new Error('Failed to fetch statuses');
      }
    } catch (error) {
      console.error('Status error:', error);
      setErrorMessage('Failed to load client statuses');
    }
  };

  // Action handlers
  const handleViewPatientFiles = () => {
    navigate(`/patientfiles2/?token=${urlToken}`);
  };

  const handleGoToMaternitySection = () => {
    navigate(`/maternity-dashboard/?token=${urlToken}`);
  };

  const submitFamilyPlanningMethod = async (fpId, methodName) => {
    try {
      const response = await fetch(urls.payfp, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: urlToken, fpId, methodName }),
      });

      if (response.ok) {
        toast.success('Submission successful. Please send the client to the cashier.');
        setSelectedPatient(null);
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setErrorMessage('Failed to submit method');
    }
  };

  const handleMethodSelection = (patient) => {
    setSelectedPatient(patient);
  };

  const handleMethodSelect = (methodName) => {
    const confirmationMessage = `Confirm ${methodName} for ${selectedPatient.first_name} ${selectedPatient.last_name}?`;
    if (window.confirm(confirmationMessage)) {
      submitFamilyPlanningMethod(selectedPatient.fp_id, methodName);
    }
  };

  const handleOpenModal = (client) => {
    setModalData({ ...client, token: urlToken });
    setShowModal(true);
  };

  // Component initialization and auto-refresh
  useEffect(() => {
    fetchFamilyPlanningPatients();
    fetchAllMethods();
    fetchClientStatuses();

    const interval = setInterval(() => {
      fetchFamilyPlanningPatients();
      fetchClientStatuses();
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  // Render helper functions
  const renderFpPatients = () => {
    if (fpPatients.length === 0) {
      return <p style={styles.noDataText}>No patients found</p>;
    }
 
    return fpPatients.map((patient) => (
      <div key={patient.fp_id} style={styles.patientCard}>
        <h4 style={styles.patientName}>{patient.first_name} {patient.last_name}</h4>
        <p style={styles.patientDetail}>Age: {patient.age}</p>
        <p style={styles.patientDetail}>Sex: {patient.sex}</p>
        <p style={styles.patientDetail}>Phone: {patient.phone_number}</p>
        <button 
          style={styles.actionButton}
          onClick={() => handleMethodSelection(patient)}
        >
          Select Method
        </button>
      </div>
    ));
  };

  const renderMethodSelection = () => {
    if (!selectedPatient) return null;

    return (
      <div style={styles.methodSelection}>
        <button 
          style={styles.closeButton}
          onClick={() => setSelectedPatient(null)}
        >
          ×
        </button>
        <h4 style={styles.selectionTitle}>
          Select Method for {selectedPatient.first_name} {selectedPatient.last_name}
        </h4>
        <ul style={styles.methodList}>
          {familyPlanningMethods.map((method, index) => (
            <li 
              key={index} 
              style={styles.methodItem}
              onClick={() => handleMethodSelect(method.name)}
            >
              {method.name} - UGX {method.price}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderClientStatuses = () => {
    if (clientStatuses.length === 0) {
      return <p style={styles.noDataText}>No clients available</p>;
    }

    return clientStatuses.map((client) => (
      <div key={client.contact_id} style={styles.statusCard}>
        <h4 style={styles.clientName}>{client.first_name} {client.last_name}</h4>
        <p style={styles.clientDetail}>Age: {client.age}</p>
        <p style={styles.clientDetail}>Sex: {client.sex}</p>
        <p style={styles.clientDetail}>
          Status: {client.status.includes('Give') ? (
            <span 
              style={styles.clickableStatus}
              onClick={() => handleOpenModal(client)}
            >
              {client.status}
            </span>
          ) : client.status}
        </p>
        <p style={styles.clientDetail}>Method: {client.method}</p>
      </div>
    ));
  };

  return (
    <div style={{ ...styles.dashboard, filter: showModal ? 'blur(0px)' : 'none' }}>
      <Topbar token={urlToken} />
      <MissingDrugs token={urlToken} />
      <ToastContainer />

      <div style={styles.container}>
        <div style={styles.patientContainer}>
          <h3 style={styles.sectionTitle}>Family Planning Clients from Triage</h3>
          {errorMessage && <p style={styles.errorText}>{errorMessage}</p>}
          <div style={styles.scrollableContent}>
            {renderFpPatients()}
          </div>
        </div>

        <div style={styles.statusContainer}>
          <h3 style={styles.sectionTitle}>Recent Family Planning Clients</h3>
          <div style={styles.scrollableContent}>
            {renderClientStatuses()}
          </div>
        </div>
      </div>

      {renderMethodSelection()}

      {showModal && (
        <GiveFPmodal
          clientDetails={modalData}
          onClose={() => setShowModal(false)}
        />
      )}

      <div style={styles.navigationButtons}>
        <div style={styles.buttonWrapper}>
          <button
            onClick={handleGoToMaternitySection}
            style={styles.navButton}
          >
            Maternity Section
          </button>
         
        </div>

        <button
          onClick={handleViewPatientFiles}
          style={styles.navButton}
        >
          View Patient Files
        </button>
      </div>
    </div>
  );
}

// Updated CSS styles
const styles = {
  dashboard: {
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    backgroundColor: '#f5f7fa',
    minHeight: '100vh',
    padding: '20px',
    color: '#333',
  },
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: '20px 0',
    gap: '20px',
    alignItems: 'flex-start',
  },
  patientContainer: {
    flex: 1,
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    height: '600px', // Fixed height
    display: 'flex',
    flexDirection: 'column',
  },
  statusContainer: {
    flex: 1,
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    height: '600px', // Fixed height to match patientContainer
    display: 'flex',
    flexDirection: 'column',
  },
  scrollableContent: {
    overflowY: 'auto',
    flex: 1,
    paddingRight: '10px', // Add some padding to prevent content from touching scrollbar
  },
  sectionTitle: {
    textAlign: 'center',
    fontSize: '1.3rem',
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: '20px',
    paddingBottom: '10px',
    borderBottom: '1px solid #eee',
  },
  patientCard: {
    backgroundColor: '#f8f9fa',
    borderRadius: '6px',
    padding: '15px',
    marginBottom: '15px',
  },
  statusCard: {
    backgroundColor: '#f8f9fa',
    borderRadius: '6px',
    padding: '15px',
    marginBottom: '15px',
  },
  patientName: {
    color: '#2980b9',
    margin: '0 0 5px 0',
  },
  clientName: {
    color: '#27ae60',
    margin: '0 0 5px 0',
  },
  patientDetail: {
    margin: '5px 0',
    fontSize: '0.9rem',
    color: '#555',
  },
  clientDetail: {
    margin: '5px 0',
    fontSize: '0.9rem',
    color: '#555',
  },
  actionButton: {
    backgroundColor: '#3498db',
    color: 'white',
    border: 'none',
    padding: '8px 15px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    marginTop: '10px',
    transition: 'background-color 0.3s',
    '&:hover': {
      backgroundColor: '#2980b9',
    }
  },
  methodSelection: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: 'white',
    padding: '25px',
    borderRadius: '8px',
    boxShadow: '0 5px 15px rgba(0,0,0,0.2)',
    zIndex: 1000,
    width: '400px',
    maxWidth: '90%',
  },
  closeButton: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    background: 'none',
    border: 'none',
    fontSize: '1.2rem',
    cursor: 'pointer',
    color: '#7f8c8d',
  },
  selectionTitle: {
    color: '#2c3e50',
    marginBottom: '15px',
    textAlign: 'center',
  },
  methodList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  methodItem: {
    padding: '10px',
    margin: '5px 0',
    backgroundColor: '#ecf0f1',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#d5dbdb',
    }
  },
  clickableStatus: {
    color: '#e74c3c',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  noDataText: {
    textAlign: 'center',
    color: '#7f8c8d',
    fontStyle: 'italic',
    margin: '20px 0',
  },
  errorText: {
    color: '#e74c3c',
    textAlign: 'center',
    margin: '20px 0',
  },
  navigationButtons: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '20px',
    margin: '30px 0',
  },
  buttonWrapper: {
    position: 'relative',
  },
  navButton: {
    backgroundColor: '#2c3e50',
    color: 'white',
    border: 'none',
    padding: '12px 25px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '1rem',
    transition: 'background-color 0.3s',
    '&:hover': {
      backgroundColor: '#1a252f',
    }
  },
 
};

export default NurseDashboard;